import type {
  AssistantMessagesPage,
  AssistantSession,
} from '@/lib/api/assistant.api'
import type { InfiniteData } from '@tanstack/react-query'
import { useMemo } from 'react'
import { assistantApi } from '@/lib/api/assistant.api'
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { queryKeys } from '../query-keys'

export function useAssistantSessionsQuery(options?: { enabled?: boolean }) {
  return useQuery<AssistantSession[]>({
    queryKey: queryKeys.assistant.sessions(),
    queryFn: () => assistantApi.listSessions(),
    enabled: options?.enabled !== false,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

export function useAssistantMessagesQuery(
  sessionId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: queryKeys.assistant.messages(sessionId ?? ''),
    queryFn: ({ pageParam }) =>
      assistantApi.getMessages(sessionId!, {
        cursor: pageParam,
        limit: 20,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage =>
      lastPage.hasMore ? (lastPage.nextCursor ?? undefined) : undefined,
    enabled: options?.enabled !== false && !!sessionId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

export function useAssistantInvalidation() {
  const queryClient = useQueryClient()

  // Stable reference across renders — callers put these in useCallback deps,
  // so a fresh object literal each render would invalidate every downstream
  // memo (LatestExchangeView → MessageBubble → ReactMarkdown components),
  // causing visible re-mounts of streamed content like ModuleCardList.
  return useMemo(
    () => ({
      invalidateSessions: () =>
        queryClient.invalidateQueries({
          queryKey: queryKeys.assistant.sessions(),
        }),
      invalidateMessages: (sessionId: string) =>
        queryClient.invalidateQueries({
          queryKey: queryKeys.assistant.messages(sessionId),
        }),
      setMessages: (sessionId: string, page: AssistantMessagesPage) =>
        queryClient.setQueryData<
          InfiniteData<AssistantMessagesPage, string | undefined>
        >(queryKeys.assistant.messages(sessionId), {
          pages: [page],
          pageParams: [undefined],
        }),
      removeSession: (sessionId: string) => {
        queryClient.removeQueries({
          queryKey: queryKeys.assistant.messages(sessionId),
        })
        queryClient.setQueryData<AssistantSession[]>(
          queryKeys.assistant.sessions(),
          old => old?.filter(s => s.id !== sessionId),
        )
      },
    }),
    [queryClient],
  )
}
